import Decimal from "decimal.js"
import {Duration} from "./Duration.js"
import {Expression} from "./Expression.js"

import {MC} from './MC.js'

const Value = {

  v: function(value, basicType = 'string', empty = false) {
    if (empty) {
      return {type: basicType, empty: true, value: ''}
    }
    if (value == undefined || value == null) {
      return {type: basicType, null: true, value: null}
    }
    if (basicType === 'collection') {
      return {type: basicType, value: MC.asArray(value)} 
    }
    if (Array.isArray(value)) {
      return Value.castToScalar(Value.fromJson(value), basicType)
    }
    if (MC.isPlainObject(value)) {
      if ('anyType' === basicType || 'dataNode' === basicType) {
        return Value.fromJson(value)
      } else {
        return Value.castToScalar(Value.v(value, 'anyType'), basicType)
      }
    }
    if ('dataNode' === basicType) {
      throw new Error('Can not cast "' + value + '" to data node value!')
    }
    if ('boolean' === basicType) {
      if (typeof value === 'string') {
        value = value.trim().toLowerCase()
        if (value === 'true') {
          return {type: basicType, value: 'true'}
        } else if (value === 'false') {
          return {type: basicType, value: 'false'}
        } else {
          throw new Error('Value "' + value + '" is not castable to boolean!');
        }
      } else {
        if (value === true) {
          return {type: basicType, value: 'true'}
        } else if (value === false) {
          return {type: basicType, value: 'false'}
        } else {
          throw new Error('Value "' + value + '" is not castable to boolean!');
        }
      }
    } else if (['integer', 'int', 'long', 'short', 'byte', 'decimal', 'double', 'float'].indexOf(basicType) > -1) {
      if (typeof value === 'string') {
        value = value.trim()
        if (value.startsWith('+')) {
          value = value.substring(1)
        }
      }
      if (['integer', 'long', 'int', 'short', 'byte'].indexOf(basicType) > -1) {
        let number = new Decimal(value)
        if (number.isInt()) {
          return {type: basicType, value: number.toFixed(0)}
        } else {
          throw new Error('Value "' + value + '" is not castable to integer!')
        }
      } else if (['float', 'double', 'decimal'].indexOf(basicType) > -1) {
        return {type: basicType, value: (new Decimal(value)).toFixed()}
      }
    } else if ('duration' === basicType) {
      var dur = new Duration()
      dur.parseIsoString(value)
      if (!dur.isValidDuration()) {
        throw new Error('Value "' + value + '" is not castable to duration!')
      }
      return {type: basicType, value: dur.toIsoString()}
    } else if (['date', 'time', 'dateTime'].indexOf(basicType) >= 0) {
      if (typeof value === 'string') {
        value = value.trim()
      }
      let lux = MC.dateTimeStringToLuxon(value)
      if (!lux.v.isValid) {
        throw new Error('Value "' + value + '" is not castable to ' + basicType + '!')
      }
      return {type: basicType, value: MC.luxonToDateTimeString(lux, basicType)}
    } else if ('hexBinary' == basicType) {
      value = ('' + value).trim()
      if (value.match(/^[0-9a-fA-F]+$/)) {
        return {type: basicType, value: value.toUpperCase()}
      } else if (value.match(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/)) {
        let expression = new Expression()
        expression.init(null, {}, null)
        value = expression.operatorDecodeBase64([Value.v(value, 'string')])
        value = expression.operatorEncodeHex([value])
        return {type: basicType, value: value.value}
      } else {
        return {type: basicType, value: '' + value}
      }
    } else if ('base64Binary' == basicType) {
      if (value.match(/^[0-9a-fA-F]+$/)) { //is hex
        value = ('' + value).trim().toUpperCase()
        let expression = new Expression()
        expression.init(null, {}, null)
        value = expression.operatorDecodeHex([Value.v(value, 'string')])
        value = expression.operatorEncodeBase64([value])
        return {type: basicType, value: value.value}
      } else {
        return {type: basicType, value: ('' + value).trim()}
      }
    } else {
      return {type: basicType, value: '' + value}
    }
  },
  dataNode: function(object) {
    if (MC.isPlainObject(object)) {
      return {type: 'anyType', value: object}
    } else {
      return Value.v(null)
    }
  },
  error: function(mess, path) {
    let val = {type: 'error', mess: mess}
    if (path) {
      val.path = path
    }
    return val
  },
  errorPathSet: function(value, path) {
    if (path && !value.path) {
      value.path = path
    }
  },
  errorAdjustMessage: function(val, mess) {
    val.mess = mess + " \n" + val.mess
  },
  getErrorMessage: function(value) {
    return (value.path ? ("Target path '" + value.path + "' \n") : "") + value.mess
  },
  isError: function(value) {
    return value.type == 'error' || value.isErrorInside
  },
  isErrorInside: function(value) {
    return value.isErrorInside
  },
  errorInside: function(value, mess) {
    if (!Value.isError(value)) {
      value.mess = mess
      value.isErrorInside = true
    }
  },
  fromJson: function(value, fromLiteral = false) {
    let res = {}
    if (Array.isArray(value)) {
      res.type = 'collection'
      res.value = []
      for (let it of value) {
        res.value.push(Value.fromJson(it, fromLiteral))
      }
    } else if (MC.isPlainObject(value)) {
      res.type = 'anyType'
      res.value = {}
      for (let key in value) {
        res.value[key] = Value.fromJson(value[key], fromLiteral)
      }
    } else {
      if (typeof value == "number") {
        res.type = 'decimal'
        res.value = Value.v(value, 'decimal').value
      } else if (typeof value == "boolean") {
        res.type = 'boolean'
        res.value = Value.v(value, 'boolean').value
      } else if (MC.isNull(value)) {
        res.type = 'string'
        res.value = null
        res.null = true
      } else if (fromLiteral) {
        res = Value.parseLiteral(value)
      } else {
        res.type = 'string'
        res.value = value
      }
    }
    return res
  },
  toJson: function(value, forceNumbers = false, asLiteral = false, emptyAsNull = false) {
    let res = null
    if (!value) {
      return res
    }
    if (value.null) {
      return null
    } else if (value.empty) {
      return emptyAsNull ? null : ""
    } else if (value.type == 'collection') {
      res = []
      for (let it of value.value) {
        res.push(asLiteral ? Value.toLiteral(it, undefined, true) : Value.toJson(it, forceNumbers, asLiteral, emptyAsNull))
      }
    } else if (value.type == 'anyType' && MC.isPlainObject(value.value)) {
      res = {}
      for (let key in value.value) {
        res[key] = asLiteral ? Value.toLiteral(value.value[key], undefined, true) : Value.toJson(value.value[key], forceNumbers, asLiteral, emptyAsNull)
      }
    } else {
      if (value.type == 'boolean') {
        res = (value.value === "true")
      } else if (Value.isNumber(value) && forceNumbers) {
        res = parseFloat(value.value)
      } else {
        res = value.value
      }
    }
    return res
  },
  isNumber: function(value) {
    return ['integer', 'int', 'long', 'short', 'byte', 'decimal', 'double', 'float'].indexOf(value.type) > -1
  },
  isDateOrTime: function(value) {
    return ['date', 'time', 'dateTime'].indexOf(value.type) >= 0
  },
  isDuration: function(value) {
    return value.type == 'duration'
  },
  isInt: function(value) {
    return ['integer', 'int', 'long', 'short', 'byte'].indexOf(value.type) > -1
  },
  isCollection: function(value) {
    return value.type == 'collection'
  },
  isCollectionNotEmpty: function(value) {
    return value.type == 'collection' && Array.isArray(value.value) && value.value.length > 0
  },
  isCollectionEmpty: function(value) {
    return value.type == 'collection' && Array.isArray(value.value) && value.value.length == 0
  },
  isDataNode: function(value) {
    return (value.type == 'anyType' || value.type == 'dataNode') && MC.isPlainObject(value.value)
  },
  isDataNodeEmpty: function(value) {
    return Value.isDataNode(value) && MC.isEmptyObject(value.value)
  },
  isNullish: function(value) {
    if (value.null === true) {
      return true
    }
    if (Value.isCollection(value)) {
      for (let item of Value.collectionValue(value)) {
        if (item && !Value.isNullish(item)) {
          return false
        }
      }
      return Value.isCollectionEmpty(value) ? false : true
    }
    if (Value.isDataNode(value)) {
      for (let k in value.value) {
        if (k && !Value.isNullish(value.value[k])) {
          return false
        }
      }
      return Value.isDataNodeEmpty(value) ? false : true
    }
    return false
  },
  hasProperty: function(value, prop) {
    return (value.type == 'anyType' || value.type == 'dataNode') && !MC.isNull(value.value[prop])
  },
  hasPropertyNotEmptyish: function(value, prop) {
    return (value.type == 'anyType' || value.type == 'dataNode') && !MC.isNull(value.value[prop]) && !Value.isEmptyish(value.value[prop])
  },
  getProperty: function(value, prop) {
    if (Value.hasProperty(value, prop)) {
      return value.value[prop]
    } else {
      return Value.v(null)
    }
  },
  setProperty: function(value, prop, val) {
    if (Value.isDataNode(value)) {
      if (!MC.isPlainObject(value.value)) {
        value.value = {}
      }
      if (Value.isNullish(val)) {
        delete value.value[prop]
      } else {
        value.value[prop] = val
      }
    }
  },
  collectionValue: function(value, i) {
    if (Value.isCollectionNotEmpty(value)) {
      return value.value
    } else {
      return []
    }
  },
  collectionItem: function(value, i) {
    if (Value.isCollectionNotEmpty(value) && value.value[i]) {
      return value.value[i]
    } else {
      return Value.v(null)
    }
  },
  collectionSize: function(value, i) {
    if (Value.isCollectionNotEmpty(value)) {
      return value.value.length
    } else {
      return 0
    }
  },
  isEmpty: function(value) {
    return value.empty === true
  },
  isEmptyish: function(value) {
    if (Value.isNullOrEmpty(value) || value.type === 'string' && typeof value.value == 'string' && value.value === '') {
      return true
    }
    if (Value.isCollection(value)) {
      for (let item of Value.collectionValue(value)) {
        if (!Value.isEmptyish(item)) {
          return false
        }
      }
      return true
    }
    if (Value.isDataNode(value)) {
      for (let item of Object.values(value.value)) {
        if (!Value.isEmptyish(item)) {
          return false
        }
      }
      return true
    }
    return false
  },
  isWhiteSpaceString: function(value) {
    return Value.isEmpty(value) || value.type === 'string' && typeof value.value == 'string' && value.value.trim() === ''
  },
  isEmptyString: function(value) {
    return Value.isEmpty(value) || value.type === 'string' && typeof value.value == 'string' && value.value === ''
  },
  isNull: function(value) {
    return value.null === true
  },
  isNullishOrEmpty: function(value) {
    return Value.isEmpty(value) || Value.isNullish(value)
  },
  isNullOrEmpty: function(value) {
    return Value.isEmpty(value) || Value.isNull(value)
  },
  castToScalar: function(value, type) {
    if (Value.isError(value)) {
      return value
    }
    if (Value.isNullish(value)) {
      return Value.v(null)
    }
    if (Value.isCollection(value)) {
      if (value.value.length == 0) {
        return Value.v(null)
      }
      for (let item of Value.collectionValue(value)) {
        if (!Value.isNull(item)) {
          value = item
          break
        }
      }
    } else if (Value.isDataNode(value)) {
      if (MC.isEmptyObject(value.value)) {
        return Value.v(null)
      }
      for (let k in value.value) {
        if (k && !Value.isNull(value.value[k])) {
          value = value.value[k]
          break
        }
      }
    }
    if (Value.isCollection(value) || Value.isDataNode(value)) {
      return Value.castToScalar(value, type)
    } else {
      return Value.v(value.value, type || value.type, Value.isEmpty(value) || (type && type !== 'string' || value.type !== 'string') && Value.isWhiteSpaceString(value)) // if casting to non-string type, empty string is considered as empty value
    }
  },
  castToCollection: function(value) {
    if (!Value.isCollection(value)) {
      return Value.isNull(value) || Value.isEmpty(value) ? value : {type: 'collection',  value: [value]}  
    }
    return value
  },
  castToDataNode: function(value) {
    if (Value.isCollection(value)) {
      value = Value.castToDataNode(Value.collectionItem(value, 0))
    }
    if (Value.isNullOrEmpty(value) || Value.isDataNode(value)) {
      return value
    }
    throw new Error('Cannot cast "' + value.type + '" (' + JSON.stringify(Value.toLiteral(value)) + ') to data node value!')
  },
  castToAnyType: function(value) {
    if (Value.isCollection(value)) {
      value = Value.castToAnyType(Value.collectionItem(value, 0))
    }
    return value
  },
  isTrue: function(value) {
    if (Value.isNullishOrEmpty(value)) {
      return false
    } else {
      return value.value == 'true'
    }
  },
  isFalse: function(value) {
    if (Value.isNullishOrEmpty(value)) {
      return false
    } else {
      return value.value == 'false'
    }
  },
  getCollectionFirstNotNull: function(value) {
    for (let v of Value.collectionValue(value)) {
      if (!Value.isNullish(v)) {
        return v
      }
    }
    return Value.v(null)
  },
  collectionDepth: function(coll) {
    if (!Value.isCollection(coll)) {
      return 0
    }
    let depth = 1
    for (let item of this.collectionValue(coll)) {
      let itemDepth = Value.collectionDepth(item) + 1
      if (itemDepth > depth) {
        depth = itemDepth
      }
    }
    return depth
  },
  extend: function(target, source, respectNull = false) {
    let name, tgt, src, copyIsArray, clone
    for (name in source.value) {
      tgt = target.value[name]
      src = source.value[name]
      if (target === src) { // Prevent never-ending loop
        continue
      }
      if (src && (Value.isDataNode(src) || (copyIsArray = Value.isCollection(src)))) {
        if (copyIsArray) {
          copyIsArray = false
          clone = tgt && Value.isCollection(tgt) ? tgt : {type: 'collection', value: []}
        } else {
          clone = tgt && Value.isDataNode(tgt) ? tgt : Value.dataNode({})
        }
        let res = Value.extend(clone, src, respectNull)
        if (!Value.isNullish(res) && !Value.isCollectionEmpty(res) && !Value.isDataNodeEmpty(res)) { // result has not null value
          target.value[name] = res
        } else if (Value.isCollectionEmpty(src) || Value.isDataNodeEmpty(src)) { // or res is null but origin value is empty collection or empty data node
          target.value[name] = src
        }
      } else {
        if (!Value.isNullish(src) || respectNull) {
          if (Value.isEmpty(src) && tgt && Value.isCollection(tgt)) {
            delete target.value[name]
          } else {
            target.value[name] = src
          }
        }
      }
    }
    return target
  },
  extendFormData: function(target, source, extendCollections = false, nodeResolver = null) {
    let name, tgt, src, clone
    for (name in source.value) {
      tgt = target.value[name]
      src = source.value[name]
      if (target === src) { // Prevent never-ending loop
        continue
      }
      if (src && Value.isCollection(src)) {
        target.value[name] = Value.v(Value.collectionValue(src).map((item, i) => {
          if (Value.isDataNode(item) || Value.isCollection(item)) {
            const targetProp = Value.getProperty(target, name)
            if (extendCollections) {
              return Value.extendFormData(Value.isCollection(targetProp) && !Value.isEmptyish(Value.collectionItem(targetProp, i)) ? Value.collectionItem(targetProp, i) : Value.isCollection(item) ? Value.v([], 'collection') : Value.dataNode({}), item, true, nodeResolver)
            } else {
              return Value.extendFormData(Value.isCollection(item) ? Value.v([], 'collection') : Value.dataNode({}), item, false, nodeResolver)
            }  
          } else {
            return item
          }
        }), 'collection')
      } else if (src && Value.isDataNode(src)) {
        if (nodeResolver && nodeResolver(tgt, src)) {
          continue
        }
        clone = tgt && Value.isDataNode(tgt) ? tgt : Value.dataNode({})
        let res = Value.extendFormData(clone, src, extendCollections, nodeResolver)
        if (!Value.isNullish(res)) {
          target.value[name] = res
        }
      } else {
        if (!Value.isNullish(src)) {
          if (Value.isEmpty(src) && tgt && Value.isCollection(tgt)) {
            delete target.value[name]
          } else {
            target.value[name] = src
          }
        }
      }
    }
    return target
  },
  parseLiteral(source, canBeContextPath = false) {
    if (source.startsWith('{') || source.startsWith('[')) {
      let data = JSON.parse(source)
      return Value.fromJson(data, true)
    } else if (source.startsWith("'")) {
      return Value.v(source.substring(1, source.length - 1), 'string')
    } else if (MC.isNumeric(source)) {
      return Value.v(source, /^-?\d+$/.test(source) ? 'integer' : 'decimal')
    } else if (source == 'true' || source == 'false') {
      return Value.v(source, 'boolean')
    } else if (source == 'null') {
      return Value.v(null)
    } else if (source == 'empty') {
      return Value.v(null, 'string', true)
    } else if (source == 'emptydn') {
      return Value.dataNode({}) 
    } else if (source == 'emptycoll') {
      return Value.v([], 'collection')
    } else if (source.indexOf('(') > 0 && source.endsWith(')')) {
      return Value.v(source.substring(source.indexOf('(') + 1, source.length - 1), source.substring(0, source.indexOf('(')))
    } else {
      if (canBeContextPath) {
        return false
      } else {
        return Value.v(source, 'string')
      }
    }  
  },
  toLiteral: function(value, prettyPrint, inside = false) {
    if (Value.isNull(value)) {
      return "null"
    } else if (Value.isEmpty(value)) {
      return "empty"
    } else if (Value.isNumber(value) || value.type == 'boolean') {
      return value.value.toString()
    } else if (Value.isCollection(value) || Value.isDataNode(value)) {
      let res = Value.toJson(value, true, true)
      if (prettyPrint !== undefined || Value.isDataNode(value) && MC.isEmptyObject(value.value) && !inside) {
        // root called from function dataToLiteral
        return JSON.stringify(res, null, prettyPrint ? 2 : null)
      } else {
        return res
      }
    } else {
      if (value.type == 'string') {
        return "'" + value.value + "'"
      } else if (value.type == 'error') {
        return 'Error: ' + value.mess
      } else {
        return value.type + '(' + value.value + ')'
      }
    }
  },
  removeNULLs: function(value) {
    if (Value.isCollectionEmpty(value) || Value.isDataNodeEmpty(value)) {
      return value
    }
    if (Value.isCollection(value)) {
      let newColl = []
      for (let item of Value.collectionValue(value)) {
        if (item && !Value.isNull(item)) { // there can be undefined in sparse collections
          let cleanItem = Value.removeNULLs(item)
          if (!Value.isNull(cleanItem)) {
            newColl.push(cleanItem)
          }
        }
      }
      return newColl.length > 0 ? Value.v(newColl, 'collection') : Value.v(null)
    } else if (Value.isDataNode(value)) {
      let newObj = {}
      for (let key in value.value) {
        let item = value.value[key]
        if (!Value.isNull(item)) {
          let cleanItem = Value.removeNULLs(item)
          if (!Value.isNull(cleanItem)) {
            newObj[key] = cleanItem
          }
        }
      }
      return MC.isEmptyObject(newObj) ? Value.v(null) : Value.dataNode(newObj)
    } else {
      return value
    }
  },
  getValue: function(value, tokens, isCollection = false) { // it is used for getting end value on path only without whole substructure from data node or context oposite of getProperty which gets property as is
    // COMPATIBILITY FLAG
    if (isCollection && !Value.isEmpty(value)) {
      value = Value.castToCollection(value)
      let res = []
      for (let i=0; i< Value.collectionSize(value); i++) {
        if (tokens.length > 1) {
          let subTokens = tokens.slice()
          subTokens.shift()
          res[i] = Value.getValue(Value.getValueByKey(value.value[i], tokens[0]), subTokens, tokens[0].endsWith('*'))
        } else {
          res[i] = Value.getValueByKey(value.value[i], tokens[0])
        }
      }
      return res.length > 0 ? Value.v(res, 'collection') : Value.v(null)
    } else if (!Value.isNullish(value)) {
      if (Value.isCollection(value)) {
        value = value.value[0]
      }
      let result = Value.getValueByKey(value, tokens[0])
      if (Value.isNullish(result) && Value.hasProperty(result, '@customwidget') && Value.hasProperty(result, 'value') && Value.isDataNode(Value.getProperty(result, 'value')) && Value.hasProperty(Value.getProperty(result, 'value'), tokens[0])) {
        result = Value.getProperty(Value.getProperty(result, 'value'), tokens[0])
      }
      if (tokens.length > 1) {
        let subTokens = tokens.slice()
        subTokens.shift()
        return Value.getValue(result, subTokens, tokens[0].endsWith('*'))
      } else {
        if (!Value.isNullish(result) || Value.isCollection(result)) {
          if (tokens[0].endsWith('*') && !Value.isEmpty(result)) {
            if (Value.isCollection(result)) {
              return Value.v([...result.value], 'collection')
            } else {
              return Value.v([result], 'collection') 
            }
          } else {
            if (Value.isCollection(result)) {
              result = result.value[0]
            }
            return result
          }
        } else {
          return Value.v(null)
        }
      }
    } else {
      return Value.v(null)
    }
  },
  getValueByKey: function(value, key) { // it is used for getting end value only without whole substructure from data node or context oposite of getProperty which gets property as is
    if (Value.isEmpty(value)) {
      return Value.v(null)
    }
    if (key.endsWith('*')) {
      key = key.substring(0, key.length - 1)
    }
    if (Value.isCollection(value)) {
      let res = []
      for (let item of value.value) {
        res.push(Value.getValueByKey(item, key))
      }
      return res.length > 0 ? Value.v(res, 'collection') : Value.v(null)
    } else {
      return Value.getProperty(value, key)
    }
  }
}

export {Value}